import { faThumbsUp as faThumbsUpRegular } from "@fortawesome/pro-regular-svg-icons/faThumbsUp";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { faReply } from "@fortawesome/pro-solid-svg-icons/faReply";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/pro-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Box, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { AxiosResponse, GenericAbortSignal } from "axios";
import { MprcButton, MprcConfirmDialog, MprcContainer, MprcIconButtonDropdown, MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator, MprcText, MprcTextMuted, MprcTypographyOnDemand } from "components";
import { ButtonCancel } from "components/buttons";
import { IconEllipsisV, IconEyeOpen, IconFilter } from "components/icons";
import { MprcDanger } from "components/MprcDanger";
import { MprcDropdown } from "components/MprcDropdown";
import { MprcDropDownItemAny } from "components/MprcIconButtonDropdown";
import { MprcRequestSnackbarErrorReport, MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcUserAvatarProfileLink } from "components/MprcUserAvatarProfileLink";
import MprcUserNameView, { userNameResolver } from "components/MprcUserNameView";
import { QuillCommands } from "components/Quill";
import { DeleteCommentResponse, DiscussionListFilter, DiscussionListOrderBy, DiscussionType, GetDiscussionDto, PaginatedDiscussion, TopicSelectorMode, UserSelectorResponsePaginatedList } from "generated/CoreApi_v1";
import { useMentionInterceptor } from "hooks/useMentionInterceptor";
import useDidUpdateEffect from "hooks/useNoInitialEffect";
import { Formaters } from "lib/Formaters";
import HtmlUtils from "lib/HtmlUtils";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { TopicSelectorHierarchicalDrawer } from "views/foundation/topic/drawers/TopicSelectorHierarchicalDrawer";
import { UserProfileDialog } from "views/foundation/user/dialogs/UserProfileDialog";
import { SelectorUsersFilter, UserSelectorDrawer } from "views/foundation/user/drawers/UserSelectorDrawer";
import { ChannelSelectorDrawer } from "views/knowledge/channels/drawers/ChannelSelectorDrawer";
import { ReportErrorOrAbuseDialog } from "views/knowledge/components/card/actions/ReportErrorOrAbuseDialog";
import { MentionData, MentionLinkDennotions } from "views/knowledge/publication/creation/components/PublicationEditor";
import { PublicationSelectorDrawer } from "views/knowledge/publication/drawers/PublicationSelectorDrawer";
import { DiscussionEditor } from "./DiscussionEditor";

////////////////////////////////////////////////////////////////////////////

type MenuOptions = "reportAbuse" | "edit" | "delete";
type MenuMentionOptions = "people" | "topic" | "channel" | "publication";
type CounterMode = "thread" | "answers" | "none";
export type DiscussionMode = "student" | "instructor";

const RECENT_PAGE_SIZE = 10;
const RELEVANCE_PAGE_SIZE = 10;

export interface CreateDiscussion {
    parentId: string | null;
    replyId: string | null;
    message: string;
    discussionType: DiscussionType;
}

interface IDiscussion extends GetDiscussionDto {
    responding: boolean;
    editing?: boolean;
    loading?: boolean;
}

////////////////////////////////////////////////////////////////////////////

export interface IPropsDiscussion {
    referenceId: string;
    title: string;
    counterMode: CounterMode;
    readOnly?: boolean;
    dialogMode?: boolean;
    disableEditPrivacy?: boolean;
    enableInstructorPrivacyOption?: boolean;
    configPeopleMention?: any;
    initialValue?: string;
    discussionMode?: DiscussionMode;
    initialListFilter?: DiscussionListFilter;
    onUpdateCommentCounter?: (totalComments: number) => void;
    onGetDiscussions: (parentId: string | null, startSequenceNum: number | null, take: number, filter: DiscussionListFilter, orderBy: DiscussionListOrderBy, discussionId: string | null) => Promise<AxiosResponse<PaginatedDiscussion>>;
    onPostNew: (discussion: CreateDiscussion) => Promise<AxiosResponse<PaginatedDiscussion>>;
    onPostUpdate: (discussionId: string, message: string, discussionType: DiscussionType) => Promise<AxiosResponse<boolean>>;
    onPostDelete: (discussionId: string) => Promise<AxiosResponse<DeleteCommentResponse>>;
    onPostLike: (discussionId: string, like: boolean) => Promise<AxiosResponse<GetDiscussionDto>>;
    onChangeDiscussionType?: (discussionType: DiscussionType) => void;
    onGetPeopleData?: (filter: SelectorUsersFilter, signal: GenericAbortSignal | undefined) => Promise<AxiosResponse<UserSelectorResponsePaginatedList>>;
}

/**
 * Componente base de discussões e comentários.
 * @param props
 */
export const DiscussionBase: React.FC<IPropsDiscussion> = (props) => {

    const {
        referenceId,
        title,
        readOnly = false,
        counterMode,
        enableInstructorPrivacyOption = false,
        disableEditPrivacy = false,
        onGetDiscussions,
        onUpdateCommentCounter,
        onPostNew,
        onPostUpdate,
        onPostLike,
        onPostDelete,
        onChangeDiscussionType,
        onGetPeopleData,
        dialogMode,
        discussionMode = "student",
        initialListFilter = DiscussionListFilter.All
    } = props;

    const idBoxContainer = `discussionBoxContainer${dialogMode ? "-dialog" : ""}`;

    const { t } = useTranslation("core");
    const theme = useTheme();

    const session = useSession();
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const [mentionDialog] = useMentionInterceptor(idBoxContainer);

    const qlEditorId = `ql${new Date().getTime()}`;
    const minHeight = 10;

    const initialContentRef = useRef<string>(props.initialValue ?? "");
    const [discussionType, setDiscussionType] = useState<DiscussionType>(DiscussionType.Public);
    const editorRef = useRef<QuillCommands | null>(null);
    const [responding, setResponding] = useState<boolean>(false);
    const [editorEmpty, setEditEmpty] = useState<boolean>(false);

    const [discussions, setDiscussions] = useState<IDiscussion[]>([]);
    const [orderBy, setOrderBy] = useState<DiscussionListOrderBy>(DiscussionListOrderBy.Recent);
    const [create, setCreate] = useState<CreateDiscussion | null>(null);
    const [deleteComment, setDeleteComment] = useState<IDiscussion | null>(null);
    const [showActions, setShowActions] = useState<boolean>(props.initialValue ? true : false);

    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalThreads, setTotalThreads] = useState<number>(0);

    const [filter, setFilter] = useState<DiscussionListFilter>(initialListFilter);
    const canComment = useRef<boolean>(false);

    const [firstLoading, setFirstLoading] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);

    const loadCount = useRef<number>(0);
    const nextLoadIndex = useRef<number | null>(null);

    const [dialog, setDialog] = useState<any>(null);
    const [error, setError] = useState<any>(null);

    const isReadOnly = readOnly || !canComment.current;

    ///
    function clearData() {
        setDiscussions([]);
        setTotalRecords(0);
        setTotalThreads(0);
        nextLoadIndex.current = null;
    }

    ///
    function getMenuAction(item: IDiscussion): MprcDropDownItemAny[] {

        const { actions } = item;

        let items: MprcDropDownItemAny[] = [];

        if (counterMode == "answers" && actions.canReportAbuse) items.push({ text: t("Discussion.Actions.ReportAbuse"), value: "reportAbuse" });
        if (actions.canEdit) items.push({ text: t("Discussion.Actions.Edit"), value: "edit" });
        if (actions.canDelete && !item.deleted) items.push({ text: (<MprcText color="error">{t("Discussion.Actions.Delete")}</MprcText>), value: "delete" });

        return items;
    }

    ///
    function appendComments(discussionId: string | null, appendValues: IDiscussion[]): IDiscussion[] {

        if (discussionId == null)
            return [...discussions, ...appendValues];

        // Os comentários de segundo nível devem ser no final da lista pois estão ordenados por ordem de data (crescente).
        const currentList = discussions;
        const index = currentList.length + 1 - currentList.slice().reverse().findIndex(p => p.parentId == discussionId);
        if (index > -1) {
            const list = ([] as IDiscussion[]).concat(currentList.slice(0, index), appendValues, currentList.slice(index));

            const discussion = list.find(p => p.discussionId == discussionId);

            if (discussion) {
                discussion.loading = false;
                discussion.responding = false;
            }
            return list;
        }

        return discussions;
    }

    ///
    function insertPostComment(
        discussionId: string | null | undefined,
        replyId: string | null | undefined,
        appendValues: IDiscussion,
        currentDiscussions: IDiscussion[]
    ): IDiscussion[] {

        if (discussionId == null)
            return [appendValues, ...currentDiscussions];

        const currentList = currentDiscussions;
        let newList = ([] as IDiscussion[]);

        var discussion = currentList.find(p => p.discussionId == discussionId);
        if (discussion != null) {
            discussion.answerCount++;
            discussion.responding = false;

            if (replyId != null) {
                // Tréplicas devem ser inseridas abaixo do comentário replicado.
                const index = currentList.findIndex(p => p.parentId == discussionId && p.discussionId == replyId) + 1;
                newList = newList.concat(currentList.slice(0, index), [appendValues], currentList.slice(index));

                var reply = newList.find(p => p.discussionId == replyId);
                if (reply) reply.responding = false;
            }
            else {
                // Réplicas devem ser inseridas no topo da lista de segundo nível
                const index = currentList.findIndex(p => p.discussionId == discussionId) + 1;
                newList = newList.concat(currentList.slice(0, index), [appendValues], discussions.slice(index));
            }
        }

        return newList;
    }

    ///
    function postUpdateComment(discussionId: string, message: string, discussionType: DiscussionType) {
        onPostUpdate(discussionId, message, discussionType)
            .then((result) => {
                if (result) {
                    setDiscussions(updateComment(discussionId, { message, modifiedOn: new Date(), editing: false, discussionType }));
                    clearEditor(true);
                    enqueueSnackbar(t("Discussion.Comment.UpdateSuccessMessage"), { variant: "success" });
                }
                else {
                    enqueueSnackbar(t("Discussion.Comment.UpdateErrorMessage"), { variant: "success" });
                }
            })
            .catch((error) => {
                setError(error);
            });
    }

    ///
    function recalculateStateAfterDelete(data: DeleteCommentResponse) {

        var newDiscussions = discussions.slice(0);

        data.deletedComments?.forEach((item) => {
            var reply = newDiscussions.find(p => p.discussionId == item.replyId);
            var parent = newDiscussions.find(p => p.discussionId == item.parentId);

            if (reply != null) reply.answerCount--;
            if (parent != null) parent.answerCount--;
            if (item.parentId == null) setTotalThreads(totalThreads - 1);

            newDiscussions = newDiscussions.filter(p => p.discussionId != item.discussionId);
        });

        data.changedComments?.forEach((item) => {
            var index = newDiscussions.findIndex(p => p.discussionId == item.discussionId);
            if (index > -1) {
                newDiscussions[index] = { ...newDiscussions[index], deleted: true, deletedBy: newDiscussions[index].createdBy };
            }
        });

        setDiscussions(newDiscussions);
        setTotalRecords(data.totalComments);
        if (onUpdateCommentCounter) onUpdateCommentCounter(data.totalComments);
    }

    ///
    function updateComment(discussionId: string, data: Partial<IDiscussion>) {
        var newDiscussions = discussions
            .slice(0)
            .map(p => ({ ...p, responding: false, editing: false, loading: false } as IDiscussion))
            ;

        var discussion = newDiscussions.find(p => p.discussionId == discussionId);
        if (discussion != null) Object.assign(discussion, data);

        return newDiscussions;
    }

    ///
    function loadComments(parentId: string | null, clearList: boolean = false) {

        loadCount.current++;

        if (parentId != null)
            setDiscussions(updateComment(parentId, {}));
        else {
            if (clearList === true) {
                setDiscussions([]);
                setTotalRecords(0);
                setTotalThreads(0);
            }
        }

        onGetDiscussions(parentId, nextLoadIndex.current, RECENT_PAGE_SIZE, filter, orderBy, null)
            .then((response) => {
                const result = response.data;
                if (result) {
                    if (parentId == null && result.items.length > 0) nextLoadIndex.current = result.items[result.items.length - 1].sequenceNum;
                    setDiscussions(appendComments(parentId, result.items as IDiscussion[]));

                    if (parentId == null) {
                        setTotalRecords(counterMode == "thread" ? result.totalThreads : result.totalRows);
                        setTotalThreads(result.totalThreads);
                    }
                    else if (counterMode == "answers") {
                        setTotalRecords(result.totalRows);
                    }
                }
            })
            .catch((error) => setError(error));
    }

    ///
    const handleLike = (item: IDiscussion) => {
        onPostLike(item.discussionId, !item.interactions.liked)
            .then((response) => {
                const result = response.data;
                if (result) {
                    setDiscussions(updateComment(item.discussionId, {
                        interactions: result.interactions
                    }));
                }
            }).catch((error) => {
                var r = MprcResolveRequestErrors(error);
                const fieldErrors: string[] = [];
                Object.entries(r.fieldsWithError ?? {}).forEach(([key, value]) => value.forEach(v => fieldErrors.push(v)));

                if (fieldErrors.length > 0)
                    fieldErrors.forEach(msg => enqueueSnackbar(msg, { variant: "error" }));
                else
                    setError(error);
            });
    };

    ///
    function clearEditor(noFocus?: boolean) {
        setResponding(false);
        setDiscussionType(DiscussionType.Public);
        setShowActions(false);
        setEditEmpty(false);

        initialContentRef.current = "";
        editorRef.current?.clear();

        if (!noFocus) setTimeout(() => editorRef.current?.focus(), 100);
    }

    ///
    const handleCancelEdit = (discussionId: string | null) => {
        if (discussionId != null) {
            setDiscussions(updateComment(discussionId, { responding: false, editing: false }));
        }

        clearEditor(true);
    };

    ///
    const handlePostNewComment = (parentId: string | null, replyId: string | null, message: string, discussionType: DiscussionType) => {
        setCreate({
            parentId,
            replyId,
            message,
            discussionType
        });
    };

    ///
    function continueInsertion(data: MentionData) {
        if (editorRef.current != null) {
            var q = editorRef.current.getQuillRef();
            const range = q.getSelection(true);
            if (range == null) return;

            if (MentionLinkDennotions.indexOf(data.denotationChar) > -1) {
                q.insertEmbed(range.index, 'performaLink', data, "api");
                q.insertText(range.index + 1, ' ', "api");
                q.setSelection(range.index + 2, 1, "silent");
            }
            else {
                q.insertEmbed(range.index, 'mention', data, "api");
                q.insertText(range.index + 1, ' ', "api");
                q.setSelection(range.index + 2, 1, "silent");
            }
        }
    }

    ///
    const handleMention = (option: MenuMentionOptions) => {
        switch (option) {
            case "people":
                setDialog(
                    <UserSelectorDrawer
                        multi={false}
                        onGetData={onGetPeopleData}
                        onClose={(user) => {
                            setDialog(null);
                            if (user?.length == 1) { continueInsertion({ id: user[0].userId, ot: "user", value: user[0].name, denotationChar: "@" }); }
                        }}
                    />);
                break;
            case "topic":
                setDialog(
                    <TopicSelectorHierarchicalDrawer
                        mode={TopicSelectorMode.Normal}
                        onClose={(topic) => {
                            setDialog(null);
                            if (topic && topic.length > 0) { continueInsertion({ id: topic[0].topicId, ot: "topic", value: topic[0].name, denotationChar: "#" }); }
                        }}
                    />);
                break;
            case "channel":
                setDialog(
                    <ChannelSelectorDrawer
                        multi={false}
                        onClose={(channel) => {
                            setDialog(null);
                            if (channel?.length == 1) { continueInsertion({ id: channel[0].channelId, ot: "channel", value: channel[0].name, denotationChar: "C" }); }
                        }}
                    />);
                break;
            case "publication":
                setDialog(
                    <PublicationSelectorDrawer
                        multi={false}
                        onClose={(pub) => {
                            setDialog(null);
                            if (pub?.length == 1) { continueInsertion({ id: pub[0].publicationId, ot: "pub", value: pub[0].title, denotationChar: "P" }); }
                        }}
                    />);
                break;
        }
    };

    ///
    const handleMenu = (option: MenuOptions, item: IDiscussion) => {
        switch (option) {
            case "edit":
                if (responding) {
                    setDialog(
                        <MprcConfirmDialog
                            open
                            message={t("Discussion.Actions.CancelEdit")}
                            onClose={confirm => {
                                confirm && handleEdit(item);
                                setDialog(null);
                            }}
                        />
                    );
                }
                else handleEdit(item);
                break;
            case "reportAbuse":
                setDialog(
                    <ReportErrorOrAbuseDialog
                        onClose={() => setDialog(null)}
                        publicationId={referenceId}
                        commentId={item.discussionId}
                        title={title}
                    />
                );
                break;
            case "delete":
                setDeleteComment(item);
                break;
        }
    };

    ///
    const handleEdit = (item: IDiscussion) => {
        initialContentRef.current = item.message;
        setDiscussions(updateComment(item.discussionId, { editing: true }));
        setDiscussionType(item.discussionType);
        setResponding(true);
        setShowActions(true);
    };

    ///
    const RenderMenu = (item: IDiscussion) => {

        const { actions } = item;

        if (isReadOnly || item.deleted || (!actions.canDelete && !actions.canEdit && !actions.canReportAbuse))
            return null;

        return (
            <MprcIconButtonDropdown
                icon={
                    <IconEllipsisV color="muted" />
                }
                buttonSize="small"
                onChange={(option) => handleMenu(option, item)}
                items={getMenuAction(item)}
            />
        );
    };

    ///
    const RenderPicture = (item: IDiscussion | null, parentId: string | null) => {

        const user: AppUser = item == null || parentId != null ? session.user : item.createdBy;

        return (
            <MprcUserAvatarProfileLink
                user={user}
                publicationId={counterMode == "answers" ? referenceId : undefined}
            />
        );
    };

    ///
    const RenderResponse = (item: IDiscussion | null, addParams: { parentId?: string, replyId?: string; }) => {

        const { parentId = null, replyId = null } = addParams;

        return (
            isReadOnly
                ? null
                : <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginTop: "10px"
                    }}
                >
                    {RenderPicture(item, parentId)}

                    <Box flex={1} marginBottom={1}>
                        <DiscussionEditor
                            id={qlEditorId}
                            minHeight={minHeight}
                            toolbar={false}
                            readOnly={false}
                            disabled={create != null}
                            initialValue={initialContentRef.current}
                            placeholder={t(counterMode == "thread" ? "Discussion.Thread.WritePlaceholder" : "Discussion.Comment.WritePlaceholder")}
                            configPeopleMention={props.configPeopleMention}
                            onFocus={(value) => value == true && showActions == false && setShowActions(true)}
                            ref={editorRef}
                            error={editorEmpty}
                        />
                        {showActions &&
                            <Grid container spacing={1}>
                                <Grid>
                                    <MprcDropdown
                                        text={t("Discussion.MentionButtonText")}
                                        title={t("Discussion.MentionButtonTitle")}
                                        items={
                                            [
                                                { text: t("Discussion.Mention.User"), value: "people", group: 1 },
                                                { text: t("Discussion.Mention.Topic"), value: "topic", group: 1 },
                                                { text: t("Discussion.Mention.Channel"), value: "channel", group: 1 },
                                                { text: t("Discussion.Mention.Publication"), value: "publication", group: 1 }
                                            ]
                                        }
                                        onChange={(selected) => handleMention(selected)}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    {
                                        counterMode == "thread" && !disableEditPrivacy && addParams.parentId == undefined && addParams.replyId == undefined &&
                                        <MprcDropdown
                                            startIcon={<IconEyeOpen />}
                                            text={t(discussionType == DiscussionType.Public
                                                ? "Discussion.DiscussionType.Public"
                                                : discussionType == DiscussionType.Private
                                                    ? "Discussion.DiscussionType.Mention"
                                                    : "Discussion.DiscussionType.Instructor")}
                                            title={t(discussionType == DiscussionType.Public
                                                ? "Discussion.DiscussionType.Public"
                                                : discussionType == DiscussionType.Private
                                                    ? "Discussion.DiscussionType.Mention"
                                                    : "Discussion.DiscussionType.Instructor")}
                                            items={
                                                enableInstructorPrivacyOption
                                                    ? [
                                                        { text: t("Discussion.DiscussionType.Public"), value: DiscussionType.Public, group: 1 },
                                                        { text: t("Discussion.DiscussionType.Mention"), value: DiscussionType.Private, group: 1 },
                                                        { text: t("Discussion.DiscussionType.Instructor"), value: DiscussionType.Instructor, group: 1 }
                                                    ]
                                                    : [
                                                        { text: t("Discussion.DiscussionType.Public"), value: DiscussionType.Public, group: 1 },
                                                        { text: t("Discussion.DiscussionType.Mention"), value: DiscussionType.Private, group: 1 }
                                                    ]
                                            }
                                            onChange={(selected) => setDiscussionType(selected)}
                                        />
                                    }
                                </Grid>
                                <Grid>
                                    <Grid container mt={1} sx={{ textAlign: "right" }} spacing={1}>
                                        <Grid>
                                            <ButtonCancel
                                                onClick={() => { handleCancelEdit(item == null ? null : item.discussionId); }}
                                                size="small"
                                                disabled={create != null}
                                            />
                                        </Grid>
                                        <Grid>
                                            <MprcButton
                                                size="small"
                                                color="success"
                                                variant="contained"
                                                text={t("Discussion.PostButtonText")}
                                                title={t("Discussion.PostButtonTitle")}
                                                disabled={create != null}
                                                onClick={() => {
                                                    if (editorRef.current!.getValueText() != "") {
                                                        const newComment = editorRef.current!.getValueHtml();
                                                        if (item != null && item.editing) postUpdateComment(item.discussionId, newComment, discussionType);
                                                        else handlePostNewComment(parentId, replyId, newComment, discussionType);
                                                    }
                                                    else {
                                                        setEditEmpty(true);
                                                        setTimeout(() => editorRef.current!.focus(), 50);
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                    </Box>
                </Box>
        );
    };

    ///
    const handleResponseClick = (discussionId: string) => {
        setResponding(true);
        setDiscussions(updateComment(discussionId, { responding: true }));
    };

    ///
    const RenderToolbar = () => {

        const discussionItems =
            [
                { text: t("Discussion.Filter.AllText"), value: DiscussionListFilter.All },
                { text: t("Discussion.Filter.CreatedByYouText"), value: DiscussionListFilter.CreatedByYou },
                { text: t("Discussion.Filter.MentionYouText"), value: DiscussionListFilter.MentionYou }
            ];

        if (counterMode != "none") {
            discussionItems.push({ text: t("Discussion.Filter.UnviewedText"), value: DiscussionListFilter.Unviewed });
            discussionItems.push({ text: t("Discussion.Filter.PrivateText"), value: DiscussionListFilter.Private });
        }

        if (discussionMode == "instructor")
            discussionItems.push({ text: t("Discussion.Filter.MentionInstructorsText"), value: DiscussionListFilter.MentionInstructors });

        return (
            <Grid container alignItems="center">
                {
                    counterMode != "answers" &&
                    <Grid>
                        <MprcIconButtonDropdown
                            menuItemCheck
                            icon={<IconFilter color={filter == DiscussionListFilter.All ? undefined : "warning"} />}
                            selected={filter}
                            onChange={(value) => setFilter(value)}
                            items={discussionItems}
                        />
                    </Grid>
                }
                <Grid size="grow">
                    <Typography variant="overline" component="p" color={filter == DiscussionListFilter.All ? undefined : theme.palette.warning.main}>
                        {
                            orderBy == DiscussionListOrderBy.Relevance
                                ? t("Discussion.Thread.RelevanceTitle")
                                : counterMode == "thread"
                                    ? t("Discussion.Thread.Count", { count: totalThreads })
                                    : t("Discussion.Comment.Count", { count: totalRecords })
                        }

                    </Typography>
                </Grid>
                <Grid>
                    <MprcDropdown
                        text={orderBy == DiscussionListOrderBy.Recent
                            ? t("Discussion.OrderBy.Recent")
                            : t("Discussion.OrderBy.Relevance")
                        }
                        title={orderBy == DiscussionListOrderBy.Recent
                            ? t("Discussion.OrderBy.Recent")
                            : t("Discussion.OrderBy.Relevance")
                        }
                        items={
                            [
                                { text: t("Discussion.OrderBy.Recent"), value: DiscussionListOrderBy.Recent, group: 1 },
                                { text: t("Discussion.OrderBy.Relevance"), value: DiscussionListOrderBy.Relevance, group: 1 }
                            ]
                        }
                        onChange={(selected) => setOrderBy(selected)}
                    />
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderMessage = (item: IDiscussion) => {
        const relativeTime = Formaters.relativeTime(item.createdOn);
        const edited = moment(item.modifiedOn).diff(item.createdOn, 'seconds') > 1
            ? t("Discussion.EditedText")
            : null;

        return (
            <Box marginRight={1}>
                <div>
                    <Grid container>
                        <Grid size="grow">
                            <MprcJoinComponents
                                components={[
                                    <MprcLink
                                        onClick={() => setDialog(
                                            <UserProfileDialog
                                                userId={item.createdBy.userId}
                                                onClose={() => setDialog(null)}
                                                onBeforeLinkClick={() =>
                                                    navigate(document.location.search, {
                                                        replace: true,
                                                        state: { ...(location.state ?? {}), referenceId: counterMode == "answers" ? referenceId : undefined }
                                                    })
                                                }
                                            />
                                        )}
                                    >
                                        <Typography
                                            variant="inherit" component="span"
                                            sx={
                                                (theme) => ({
                                                    ...theme.extended.feed["authorPrimary"],
                                                })
                                            }
                                            title={userNameResolver(item.createdBy, t, session.user)}
                                        >
                                            <MprcUserNameView user={item.createdBy} />
                                        </Typography>
                                    </MprcLink>

                                    , <Typography variant="caption">{relativeTime} {edited}</Typography>

                                    , item.discussionType != DiscussionType.Public && item.parentId == null && item.replyId == null
                                        ? <Typography variant="caption" color={theme.palette.warning.main}>
                                            {
                                                t(
                                                    item.discussionType == DiscussionType.Private
                                                        ? (item.createdBy.userId != session.user.userId ? "Discussion.DiscussionType.PostToMentions" : "Discussion.DiscussionType.PostToOwnerAndMentions")
                                                        : (item.createdBy.userId != session.user.userId ? "Discussion.DiscussionType.PostToInstructors" : "Discussion.DiscussionType.PostToOwnerAndInstructors")
                                                )
                                            }
                                        </Typography>
                                        : null
                                ]}
                                separatorVariant="bullet"
                            />
                        </Grid>
                        <Grid>
                            {RenderMenu(item)}
                        </Grid>
                    </Grid>
                </div>
                <div>
                    <Grid container spacing={1}>
                        {
                            item.interactions.hasNews && item.createdBy.userId != session.user.userId &&
                            <Grid>
                                <Box sx={(theme) =>
                                ({
                                    height: "100%",
                                    width: "4px",
                                    color: theme.palette.warning.main,
                                    backgroundColor: theme.palette.warning.main
                                })
                                } />
                            </Grid>
                        }
                        <Grid size="grow">
                            <Box display="flex"
                                sx={{
                                    "& p": {
                                        margin: "0px",
                                    }
                                }}
                            >
                                {
                                    item.repliedBy && item.repliedBy != null &&
                                    <MprcLink
                                        onClick={() =>
                                            () => setDialog(
                                                <UserProfileDialog
                                                    userId={item.repliedBy!.userId}
                                                    onClose={() => setDialog(null)}
                                                    onBeforeLinkClick={() =>
                                                        navigate(document.location.search, {
                                                            replace: true,
                                                            state: { ...(location.state ?? {}), referenceId: counterMode == "answers" ? referenceId : undefined }
                                                        })
                                                    }
                                                />
                                            )}
                                    >
                                        <Typography
                                            variant="inherit" component="span"
                                            sx={
                                                (theme) => ({
                                                    ...theme.extended.feed["authorPrimary"],
                                                })
                                            }
                                            title={userNameResolver(item.repliedBy, t, session.user)}
                                        >
                                        </Typography>
                                        <FontAwesomeIcon icon={faReply} /> <MprcUserNameView user={item.repliedBy} />&nbsp;
                                    </MprcLink>
                                }


                                {
                                    item.deleted == true && item.abuseReported === false
                                        ? <MprcDanger>{t("Discussion.DeletedText", { name: userNameResolver(item.deletedBy!, t, session.user) })}</MprcDanger>
                                        : item.deleted && item.abuseReported === true
                                            ? <MprcDanger>{t("Discussion.Comment.BlockedText")}</MprcDanger>
                                            : <MprcTypographyOnDemand
                                                maxRows={4} variant="inherit"
                                                readLessText={t("Discussion.ViewLessText")}
                                                readMoreText={t("Discussion.ViewMoreText")}
                                            >
                                                <span style={{ color: theme.palette.text.primary }} dangerouslySetInnerHTML={{ __html: HtmlUtils.textToHtml(item.message) }}></span>
                                            </MprcTypographyOnDemand>
                                }

                            </Box>
                        </Grid>
                    </Grid>
                </div>
            </Box>
        );
    };

    ///
    function RenderGlobalLoadMore() {
        if (orderBy == DiscussionListOrderBy.Relevance)
            return null;

        const total = discussions.filter(p => p.parentId == null).length;
        return (totalThreads > total)
            ? (
                <MprcContainer margin="dense">
                    <MprcButton
                        size="small"
                        color="muted"
                        variant="outlined"
                        text={t("Discussion.Comment.LoadMoreButtonText")}
                        title={t("Discussion.Comment.LoadMoreButtonText")}
                        onClick={() => loadComments(null)}
                    />
                </MprcContainer>
            )
            : null;
    }

    ///
    const RenderSocial = (item: IDiscussion) => {
        const userId = session.user.userId;
        var total = discussions.filter(p => p.parentId == item.discussionId).length;

        return (
            <Grid container direction="column" alignItems="flex-start" alignContent="start" spacing={0.5}>
                <Grid>
                    {
                        !responding && (
                            <>
                                <Grid container alignItems="center" alignContent="center" spacing={2}>
                                    <Grid>
                                        <Stack direction="row" alignItems="center">
                                            <IconButton
                                                sx={
                                                    (theme) => ({
                                                        ...theme.typography[item.interactions.liked ? "iconTextHighlight" : "iconText"],
                                                    })
                                                }
                                                disabled={item.createdBy.userId == userId || isReadOnly || item.deleted}
                                                size="small"
                                                onClick={() => handleLike(item)}
                                            >
                                                <FontAwesomeIcon
                                                    icon={item.interactions.liked ? faThumbsUpSolid : faThumbsUpRegular} fixedWidth
                                                />
                                            </IconButton>
                                            <Typography variant="caption" mt={0.5}>
                                                <MprcTextMuted>
                                                    {`${item.interactions.likes}`}
                                                </MprcTextMuted>
                                            </Typography>
                                        </Stack>
                                    </Grid>
                                    {
                                        canComment.current && !item.deleted &&
                                        <Grid>
                                            <MprcButton
                                                color={"muted"}
                                                size="small"
                                                onClick={
                                                    () => { handleResponseClick(item.discussionId); }}
                                            >
                                                <Typography variant="caption" fontWeight="bold">
                                                    {t("Discussion.AnswerButtonText")}
                                                </Typography>
                                            </MprcButton>
                                        </Grid>
                                    }
                                </Grid>
                            </>
                        )
                    }
                </Grid>

                <Grid>
                    {
                        item.answerCount > 0 && item.parentId == null && total == 0 && !responding
                            ? (
                                <MprcButton
                                    color={"primary"}
                                    size="small"
                                    onClick={
                                        () => {
                                            loadComments(item.discussionId);
                                        }
                                    }
                                >
                                    <Typography variant="caption" color="primary" fontWeight="bold">
                                        <span>{t("Discussion.ViewAnswersMessage", { count: item.answerCount })} <FontAwesomeIcon icon={faCaretDown} /></span>
                                    </Typography>
                                </MprcButton>
                            )
                            : null
                    }
                    {
                        item.answerCount > 0 && total > 0 && !responding
                            ? (
                                <MprcButton
                                    color={"primary"}
                                    size="small"
                                    sx={{ marginBottom: theme.spacing(3) }}
                                    onClick={
                                        () => {
                                            setDiscussions(discussions.filter(p => p.parentId != item.discussionId));
                                        }
                                    }
                                >
                                    <Typography variant="caption" color="primary" fontWeight="bold">
                                        {t("Discussion.HideAnswersButtonText")}
                                    </Typography>
                                </MprcButton>
                            )
                            : null
                    }
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderFooter = (item: IDiscussion) => {
        return (
            <Grid container alignItems="center">
                <Grid size="grow">
                    {RenderSocial(item)}
                </Grid>
            </Grid>
        );
    };

    ///
    const RenderComment = (parentId: string | null) => {

        var discussionItems = parentId == null
            ? discussions.filter(p => p.parentId == parentId)
            : discussions.filter(p => p.parentId == parentId).sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime());

        return (
            <>
                {
                    discussionItems.map((item, index) => {

                        var total = discussions.filter(p => p.parentId == item.discussionId).length;

                        return (
                            <Box
                                id={`discussion-item-${index}`}
                                display="flex"
                                alignItems="flex-start"
                                marginTop={0} key={item.discussionId}
                            >

                                {/* 1a Coluna */}
                                {item.editing ? null : RenderPicture(item, null)}

                                {/* 2a Coluna */}
                                <Box flex={1}>
                                    {item.editing ? RenderResponse(item, {}) : RenderMessage(item)}

                                    <Box display="block" marginBottom={2}>
                                        {item.editing ? null : RenderFooter(item)}

                                        {/* Respondendo um item de Segundo Nível ou maior */}
                                        {item.responding && parentId == null ? RenderResponse(item, { parentId: item.discussionId }) : null}

                                        {/* Comentários */}
                                        {total > 0 ? RenderComment(item.discussionId) : null}

                                    </Box>

                                    {/* Respondendo em Segundo Nível ou maior */}
                                    {item.responding && parentId != null ? RenderResponse(item, { parentId, replyId: item.discussionId }) : null}

                                </Box>
                            </Box>
                        );
                    })
                }
            </>
        );
    };

    // Ao carregar
    useEffect(() => {
        async function getData() {
            try {
                const response = await onGetDiscussions(null, null, orderBy == DiscussionListOrderBy.Relevance ? RELEVANCE_PAGE_SIZE : RECENT_PAGE_SIZE, filter, orderBy, null);
                const result = response.data;
                if (result) {
                    if (result.items.length > 0) nextLoadIndex.current = result.items[result.items.length - 1].sequenceNum;
                    setDiscussions(result.items as IDiscussion[]);
                    setTotalRecords(counterMode == "thread" ? result.totalThreads : result.totalRows);
                    setTotalThreads(result.totalThreads);

                    if (firstLoading) {
                        canComment.current = result.canComment;
                    }
                }

            } catch (e) {
                setError(e);
            }
            finally {
                setLoading(false);
                setFirstLoading(false);
            }
        }

        if (loading || firstLoading) {
            getData();
        }
    }, [loading, firstLoading]);

    // Ao fazer uma postagem
    useDidUpdateEffect(() => {
        async function postData() {
            try {
                const response = await onPostNew(create!);
                const result = response.data;

                if (result?.items && result.items.length > 0) {
                    var newComment = result.items[0] as IDiscussion;
                    setDiscussions(currentDiscussions => insertPostComment(newComment.parentId, newComment.replyId, newComment, currentDiscussions));

                    setTotalRecords(total => total + 1);
                    if (newComment.parentId == null) setTotalThreads(threads => threads + 1);
                    onUpdateCommentCounter && onUpdateCommentCounter(totalRecords + 1);

                    enqueueSnackbar(t("Discussion.Comment.PostSuccessMessage"), { variant: "success" });
                }

                clearEditor();
            } catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setCreate(null);
            }
        }

        if (create) postData();

    }, [create]);

    // Ao excluir um comentário
    useDidUpdateEffect(() => {
        async function postData() {
            try {
                const response = await onPostDelete(deleteComment!.discussionId);
                const result = response.data;
                recalculateStateAfterDelete(result);

                enqueueSnackbar(t("Discussion.Comment.DeleteSuccessMessage"), { variant: "success" });
            } catch (e) {
                MprcRequestSnackbarErrorReport(e);
            }
            finally {
                setDeleteComment(null);
            }
        }

        if (deleteComment) postData();
        else {
            // Carregar mais comentários?
            if (discussions.length > 0 && discussions.length < 5 && discussions[discussions.length - 1].sequenceNum == (nextLoadIndex.current ?? 0))
                loadComments(null);
        }

    }, [deleteComment]);

    // Ao selecionar uma ordem
    useDidUpdateEffect(() => {
        clearData();
        setLoading(true);
    }, [orderBy]);

    // Ao editar uma resposta
    useDidUpdateEffect(() => {
        if (responding == true) {
            setTimeout(() => editorRef.current?.focus(), 100);
        }
    }, [responding]);

    // Ao selecionar um filtro
    useDidUpdateEffect(() => {
        clearData();
        setLoading(true);
    }, [filter]);

    // Ao alterar o discussionType
    useEffect(() => {
        if (onChangeDiscussionType) onChangeDiscussionType(discussionType);
    }, [discussionType]);

    return (
        <div id={idBoxContainer}>
            {mentionDialog}
            {dialog}

            <MprcRequestErrorReport error={error} />

            {!firstLoading && !isReadOnly && RenderToolbar()}

            {!firstLoading && isReadOnly && discussions.length == 0
                ? <Alert severity="info">{t("Discussion.Comment.EmptyData")}</Alert>
                : (
                    <Box display="flex" flexWrap="wrap" width="auto">
                        <Box sx={{
                            width: "100%",
                            position: "relative",
                        }}
                        >
                            <MprcContainer margin="dense">
                                {!responding && RenderResponse(null, {})}
                            </MprcContainer>
                            <Box id="discussion-container-scroll" sx={dialogMode ? { overflowY: "auto", maxHeight: "40vh", paddingLeft: 0 } : { marginTop: 1.5 }} className={dialogMode ? AppGlobalClassKeys.thinScrollbar : undefined}>
                                {RenderComment(null)}
                                {RenderGlobalLoadMore()}
                            </Box>
                            {
                                (loading || create != null) &&
                                <MprcSpinnerIndicator />
                            }
                        </Box>
                    </Box>
                )
            }
        </div>
    );
};
